import { SidePanelLayout } from '@brightdrop/side-panel';
import { type ErrorInfo, type ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SIDEKICK_PANEL_WIDTH } from '../../constants';
import type {
  AcceptSidekickConsent,
  EnsureSidekickConversation,
  GetSidekickConsentStatus,
  SubmitSidekickFeedback,
} from '../../contexts/SidekickApiContext';
import { SidekickApiProvider } from '../../contexts/SidekickApiProvider';
import { SidekickConfigProvider } from '../../contexts/SidekickConfigProvider';
import { useIsSidekickEnabled } from '../../flag';
import { useSidekickActingOrganization } from '../../hooks/useSidekickActingOrganization';
import {
  type SidekickLifecycleCallback,
  useSidekickLifecycleEvents,
} from '../../lifecycle';
import { closePanel, selectIsPanelOpen } from '../../state/aiSidekickSlice';
import type { SuggestionChipSet } from '../../state/chatTypes';
import AISidekickPanel from '../AISidekickPanel';
import SidekickErrorBoundary from '../SidekickErrorBoundary';

export type SidekickLayoutShellProps = {
  /** The consumer's primary content column, forwarded into `SidePanelLayout` as-is. */
  mainContent: ReactNode;
  /**
   * Body rendered inside the Sidekick panel below the branded header.
   * When omitted, the panel default body is `<ChatInterface />`.
   * Forwarded onto `<AISidekickPanel>` as `children`.
   */
  panelBody?: ReactNode;
  /**
   * Top offset (px) applied to the panel and scrim so the drawer
   * lands below a fixed top nav.
   * @default 0
   */
  topOffsetPx?: number;
  /**
   * Viewport width (px) at which the panel flips from persistent push
   * to temporary overlay. Forwarded to both the layout and the panel
   * so the two thresholds cannot drift. Defaults to
   * `SIDE_PANEL_DEFAULT_OVERLAY_BREAKPOINT_PX`.
   */
  overlayBelowPx?: number;
  /**
   * Optional hook fired AFTER the package resets the chat from the
   * in-chat "Start a new chat?" prompt. The package owns the reset; use
   * this for analytics, tracing, or app-specific cleanup.
   */
  onNewConversation?: () => void;
  /**
   * Fired when the panel-scoped error boundary catches a render error.
   * Hosts wire this to their telemetry SDK (e.g.,
   * `datadogRum.addError`). When omitted, the boundary falls back to
   * `console.error` so failures are still observable in dev tools.
   */
  onPanelError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Optional host hook fired at each point in the user's use of Sidekick. */
  onLifecycleEvent?: SidekickLifecycleCallback;
  /** `data-testid` forwarded onto the outer flex-row wrapper. */
  rootTestId?: string;
  /** `data-testid` forwarded onto the inner main-column wrapper. */
  mainColumnTestId?: string;
  /**
   * IANA timezone identifier (e.g. `America/New_York`) forwarded to the
   * internal {@link SidekickConfigProvider}, so dates render in the
   * user's zone. Falls back to the browser's resolved zone.
   */
  timeZone?: string;
  /**
   * BCP-47 locale tag (e.g. `en-US`) forwarded to the internal
   * {@link SidekickConfigProvider}. When omitted, the provider falls
   * back to `i18n.language` reported by the active i18next instance.
   */
  locale?: string;
  /**
   * Stable signed-in-user identifier forwarded to the internal
   * {@link SidekickConfigProvider}. Used only to scope the localStorage
   * consent fallback so a shared browser never leaks one user's
   * acceptance to another. No effect once `getConsentStatus` /
   * `acceptConsent` are injected (identity is owned server-side there).
   */
  userId?: string;
  /**
   * Suggestion-chip dataset forwarded onto the default
   * `<ChatInterface />` rendered inside the panel. Ignored when the
   * consumer supplies a custom `panelBody`.
   */
  chipSet?: SuggestionChipSet;
  /**
   * Forwarded onto the default `<ChatInterface />` so the panel
   * zero-state renders skeleton chips while the host's suggestion
   * source is in flight. Ignored when the consumer supplies a custom
   * `panelBody`.
   */
  isChipSetLoading?: boolean;
  /**
   * Absolute base URL of the Sidekick API service (e.g.
   * `${globalThis.appConfig.apiBaseUrl}/sidekick`), forwarded to the
   * internal {@link SidekickApiProvider}. Required for the SSE and sync
   * transports; ignored while the Sidekick flag is off.
   */
  apiBaseUrl: string;
  /** Optional fleet identifier applied to each Sidekick message request. */
  fleetId?: string;
  /** Optional host interface identifier used for backend experiment routing. */
  interfaceName?: string;
  /**
   * Organization selected during a view-as session. Sidekick sends it
   * as `x-bd-organization` and starts a new conversation when it changes.
   * Omit it when the user is acting as themselves.
   */
  actingOrganizationId?: string;
  /** Injected callback that creates or returns the active conversation. */
  ensureConversation?: EnsureSidekickConversation;
  /** Injected callback that submits assistant-message feedback. */
  submitFeedback?: SubmitSidekickFeedback;
  /**
   * Injected callback that reads consent status. Pass together with
   * `acceptConsent`; incomplete pairs use the localStorage default keyed by
   * `userId`.
   */
  getConsentStatus?: GetSidekickConsentStatus;
  /** Injected callback that records consent. Pass with `getConsentStatus`. */
  acceptConsent?: AcceptSidekickConsent;
};

/**
 * Composes `SidePanelLayout`, `AISidekickPanel`, and
 * `SidekickErrorBoundary`, and self-wires panel open state from the
 * package's `aiSidekickSlice`. Also owns the
 * {@link SidekickConfigProvider} so consumers do not need to mount it
 * themselves — passing `timeZone` / `locale` directly on the shell
 * keeps the integration boundary at one component.
 *
 * - Returns `mainContent` verbatim when the Sidekick feature flag is
 *   off: no panel, no layout transition, and no config provider, so
 *   `useSidekickConfig` in the main content falls back to its defaults.
 * - Scopes the error boundary to the panel slot only; a panel render
 *   failure does not take down the consumer's main content.
 * - Pins the panel width to `SIDEKICK_PANEL_WIDTH`. Consumers may
 *   override the responsive overlay threshold via `overlayBelowPx`.
 */
function SidekickLayoutShell({
  mainContent,
  panelBody,
  topOffsetPx,
  overlayBelowPx,
  onNewConversation,
  onPanelError,
  onLifecycleEvent,
  rootTestId,
  mainColumnTestId,
  timeZone,
  locale,
  userId,
  chipSet,
  isChipSetLoading,
  apiBaseUrl,
  fleetId,
  interfaceName,
  actingOrganizationId,
  ensureConversation,
  submitFeedback,
  getConsentStatus,
  acceptConsent,
}: SidekickLayoutShellProps) {
  const dispatch = useDispatch();
  const isPanelOpen = useSelector(selectIsPanelOpen);
  const isSidekickEnabled = useIsSidekickEnabled();
  useSidekickActingOrganization(actingOrganizationId);
  useSidekickLifecycleEvents(isSidekickEnabled ? onLifecycleEvent : undefined);

  const handleClose = useCallback(() => {
    dispatch(closePanel());
  }, [dispatch]);

  if (!isSidekickEnabled) {
    return <>{mainContent}</>;
  }

  return (
    <SidekickApiProvider
      apiBaseUrl={apiBaseUrl}
      fleetId={fleetId}
      interfaceName={interfaceName}
      ensureConversation={ensureConversation}
      submitFeedback={submitFeedback}
      getConsentStatus={getConsentStatus}
      acceptConsent={acceptConsent}
    >
      <SidekickConfigProvider
        timeZone={timeZone}
        locale={locale}
        userId={userId}
      >
        <SidePanelLayout
          mainContent={mainContent}
          panel={
            <SidekickErrorBoundary onError={onPanelError}>
              <AISidekickPanel
                open={isPanelOpen}
                onClose={handleClose}
                topOffsetPx={topOffsetPx}
                overlayBelowPx={overlayBelowPx}
                onNewConversation={onNewConversation}
                chipSet={chipSet}
                isChipSetLoading={isChipSetLoading}
              >
                {panelBody}
              </AISidekickPanel>
            </SidekickErrorBoundary>
          }
          isPanelOpen={isPanelOpen}
          panelWidth={SIDEKICK_PANEL_WIDTH}
          overlayBelowPx={overlayBelowPx}
          rootTestId={rootTestId}
          mainColumnTestId={mainColumnTestId}
        />
      </SidekickConfigProvider>
    </SidekickApiProvider>
  );
}

export default SidekickLayoutShell;
